import { ApplicationRef, effect, EnvironmentInjector, Injectable, signal } from '@angular/core';
import { HdwMeta } from './hdw-config/hdw-meta';
import { HwdSysteem } from './hdw-config/HdwSysteem';
import { MainboardType } from './hdw-config/hdw-konfig-types';
import { NumberPopupComponent } from './hdw/number-popup/number-popup.component';
import { createCustomElement, NgElement, WithProperties } from '@angular/elements';
import { AnalogIn, DigitalOut, IOType, KanaalUpdate, RangedKanaal, RangedKanaalAssign } from '../props/PropTypes';
import { JSBridgeService } from '../core/jsbridge.service';
import { KanaalService } from './kanaal.service';
import { KopConfigService } from '../core/kop-config.service';
import { getAvailableRanges, getNextAvailableKanaal } from './kanaal.utils';

@Injectable({
  providedIn: null
})
export class HwdConfigService {

  constructor(
    private injector: EnvironmentInjector,
    private jsBridgeService: JSBridgeService,
    private kanaalService: KanaalService,
    private kopConfigService: KopConfigService
  ) {
    const PopupElement = createCustomElement(NumberPopupComponent, { injector });
    // Register the custom element with the browser.
    let customPopupElement = customElements.get('popup-element');
    if (customPopupElement == undefined) {
      customElements.define('popup-element', PopupElement);
    }
    effect(async () => {
      let system = this.system();
      if (system != undefined) {
        setTimeout(() => {
          localStorage.setItem('hdwSystem', JSON.stringify(system.toSerializable()));
        }, 10);
      }
    });
  }


  private _meta: HdwMeta = new HdwMeta();

  public meta(): HdwMeta {
    return this._meta;
  }

  public system = signal<HwdSysteem | undefined>(undefined, { equal: (a, b) => false });
  public isDirty = signal(false);

  private findUpdates(oldIos: RangedKanaal[], newIos: RangedKanaal[]) {

    let updateSet: KanaalUpdate[] = [];
    for (let oldIo of oldIos) {
      let newIo = newIos.find(io => io.ioType.equals(oldIo.ioType));
      if (newIo != undefined && newIo.start != oldIo.start) {
        let offset = newIo.start - oldIo.start;
        console.log(`Updating: ${oldIo.ioType.toString()} ${oldIo.start} -> ${oldIo.start + offset} : Offset ${offset}`)
        updateSet.push({
          ranged: oldIo,
          offset: offset
        });
      }
    }
    return updateSet;
  }

  public getUpdateSet(system: HwdSysteem) {
    let oldSystem = this.system();
    let updateSet: KanaalUpdate[] = [];
    if (oldSystem != undefined) {
      let oldBusTypes = oldSystem.getBusTypes();
      for (let busType of oldBusTypes) {
        let oldRacks = oldSystem.getRacks(busType);
        for (let rack of oldRacks) {
          for (let oldKaart of rack.kaarten) {
            let newKaart = system.getKaart(oldKaart.id);
            if (newKaart != undefined) {
              let oldIos = oldKaart.getIOs();
              let newIos = newKaart.getIOs();
              let newUpdates = this.findUpdates(oldIos, newIos);
              if (newUpdates.length > 0) {
                updateSet = updateSet.concat(newUpdates);
              }
            }
          }
        }
      }
    }
    return updateSet;
  }

  public tempKanaal: Record<string, RangedKanaalAssign> = {};

  public registerTempKanaal(key: string, kanaal: RangedKanaalAssign) {
    this.tempKanaal[key] = kanaal;
  }

  public unregisterTempKanaal(key: string) {
    delete this.tempKanaal[key];
  }

  public availableRanges(kanaalType: IOType) {
    let s = this.system();
    if (s == undefined || kanaalType == undefined) {
      return [];
    }
    let hdwAvailableRanges = getAvailableRanges(s);
    return hdwAvailableRanges[kanaalType.toString()];
  }

  public dummyEntries:Array<RangedKanaal> = [{
    ioType:DigitalOut,
    start: 255,
    range: 1
  }];

  private isDummyEntry(kanaal: RangedKanaal) {
    return this.dummyEntries.find(b => b.ioType.equals(kanaal.ioType) && b.start == kanaal.start) != undefined;
  }

  public getCurrentRange(kanalen: RangedKanaalAssign[], type: IOType) {
    let minMax = this.kanaalService.findMinMaxKanaal(kanalen, type);
    if (minMax.max - minMax.min <= 0) {
      return undefined;
    }
    let ret: RangedKanaal = {
      ioType: type,
      start: minMax.min,
      range: minMax.max - minMax.min
    }
    return ret;
  }

  public getRangedStart(oldKanalen: RangedKanaalAssign[], kanalen: RangedKanaalAssign[], type: IOType) {
    let minMax = this.kanaalService.findMinMaxKanaal(oldKanalen, type);
    let newMinMax = this.kanaalService.findMinMaxKanaal(kanalen, type);
    let range = newMinMax.max - newMinMax.min;
    let nextStart = this.getNextStart(type, false,  range, 10000);
    let ret: RangedKanaal = {
      ioType: type,
      start:  nextStart.outOfRange ? minMax.max : nextStart.start,
      range: newMinMax.max - newMinMax.min
    }
    if (ret.range <= 0) {
      return undefined;
    }
    return ret;

  }

  public getNextStart(kanaalType: IOType, virtualAI: boolean, range: number, maxKanaal: number): {start: number, outOfRange: boolean} {
    let kfg = this.jsBridgeService.getBridgeSync().getConfig();
    let oldKanalen = this.kanaalService.getKonfigKanalen(kfg);
    Object.entries(this.tempKanaal).map(([key, val]) => val).filter(k => k.rangedKnaal.ioType.equals(kanaalType)).forEach(k => {
      oldKanalen.push(k)
    });
    oldKanalen = oldKanalen.filter(k => !this.isDummyEntry(k.rangedKnaal));
    let minMax = this.kanaalService.findMinMaxKanaal(oldKanalen, kanaalType);
    let candidate = getNextAvailableKanaal(minMax.max, this.availableRanges(kanaalType));
    if (candidate == 0) {
      return { start: 0, outOfRange: true };
    }
    if ((AnalogIn.equals(kanaalType) && (range == 2)) || virtualAI) {
      return { start: Math.min((candidate + 1) >> 1 << 1, maxKanaal) , outOfRange: false };
    }
    return { start: Math.min(candidate, maxKanaal), outOfRange: false };

  }

  public applyUpdates(updateSet: KanaalUpdate[]) {
    let kfg = this.jsBridgeService.getBridgeSync().getModuleConfig();
    if (kfg != undefined) {
      this.kanaalService.applyUpdates(kfg, updateSet);
      this.kopConfigService.refresh();
    }
  }

  public updateSystem(system: HwdSysteem | undefined) {
    this.system.set(system);
    this.isDirty.set(true);
  }

  public getMainboards(): MainboardType[] {
    return this._meta.mainBoards;
  }

  public chooseNumber() {
    let p = new Promise<number | undefined>((resolve, reject) => {
      const popupEl: NgElement & WithProperties<NumberPopupComponent> = document.createElement(
        'popup-element',
      ) as any;
      // Listen to the close event
      popupEl.addEventListener('closed', () => {
        document.body.removeChild(popupEl);
        resolve(undefined);
      });
      popupEl.addEventListener('save', (e) => {
        console.log(e);
        document.body.removeChild(popupEl);
        resolve((e as CustomEvent).detail as number);
      });
      // Set the message
      // Add to the DOM
      document.body.appendChild(popupEl);

    });

    return p;
  }


}
